// The backend owns deduplication and retention; the client only fingerprints the request.
// Header name and UUID format are the backend's contract (RejectsDuplicateOperations, OperationLock).
export const IDEMPOTENCY_HEADER = 'X-Operation-Key'

const ELIGIBLE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE'])
const EXCLUDED_RESPONSE_TYPES = new Set(['arraybuffer', 'blob', 'stream'])
// Auth flows only: a replayed key here could block a legitimate re-authentication.
export const EXCLUDED_PATH_SEGMENTS = new Set(['login', 'logout', 'otp', 'session', 'sessions', 'token', 'tokens'])
const JSON_CONTENT_TYPE = /^application\/(?:[\w!#$%&*.^`~-]*\+)?json(?:;.*)?$/i
const RELATIVE_URL_BASE = 'https://idempotency.local'

export type IdempotencyRequest = {
  url: string
  method?: string
  headers?: HeadersInit
  query?: Record<string, unknown>
  body?: unknown
  responseType?: string
  baseURL?: string
}

const getRequestUrl = ({ url, baseURL }: IdempotencyRequest): URL =>
  new URL(url, new URL(baseURL ?? '', RELATIVE_URL_BASE))

const getMethod = (request: IdempotencyRequest): string => (request.method ?? 'GET').toUpperCase()

const getHeaders = (request: IdempotencyRequest): Headers => new Headers(request.headers)

/* 
  Load-bearing: a binary body often has no Content-Type yet at request time, and every
  FormData would otherwise serialize to "{}" and collide with every other upload.
*/
const isBinaryBody = (body: unknown): boolean =>
  body instanceof FormData ||
  body instanceof Blob ||
  body instanceof URLSearchParams ||
  body instanceof ReadableStream ||
  body instanceof ArrayBuffer ||
  ArrayBuffer.isView(body)

// Key order must not change the hash; array order must, since it can carry intent. Never mutates the body.
// Sorted by `<` on code points, not localeCompare: the signature must hash identically in every locale.
const canonicalizeUnknown = (_: string, value: unknown): unknown =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).sort(([leftKey], [rightKey]) => (leftKey < rightKey ? -1 : 1)))
    : value

const canonicalizeJson = (value: unknown): string => {
  const jsonValue = typeof value === 'string' ? JSON.parse(value) : value
  const result = JSON.stringify(jsonValue, canonicalizeUnknown)

  if (result === undefined) throw new TypeError('Request body is not JSON serializable')

  return result
}

const hasJsonBodyType = (request: IdempotencyRequest): boolean => {
  const { body } = request
  if (body === undefined || body === null) return true
  if (isBinaryBody(body)) return false

  const contentType = getHeaders(request).get('Content-Type')

  return !contentType || JSON_CONTENT_TYPE.test(contentType)
}

const normalizePath = (request: IdempotencyRequest): string => {
  const pathname = getRequestUrl(request).pathname

  return pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname
}

const isAdminApiPath = (path: string): boolean => path === '/admin/api' || path.startsWith('/admin/api/')

const hasExcludedPathSegment = (path: string): boolean =>
  path.split('/').some((segment) => EXCLUDED_PATH_SEGMENTS.has(segment.toLowerCase()))

export const hasExplicitIdempotencyKey = (request: IdempotencyRequest): boolean =>
  getHeaders(request).has(IDEMPOTENCY_HEADER)

export const isIdempotencyEligible = (request: IdempotencyRequest): boolean => {
  if (!ELIGIBLE_METHODS.has(getMethod(request))) return false

  const responseType = request.responseType?.toLowerCase()
  if (responseType && EXCLUDED_RESPONSE_TYPES.has(responseType)) return false
  if (!hasJsonBodyType(request)) return false

  const path = normalizePath(request)

  return isAdminApiPath(path) && !hasExcludedPathSegment(path)
}

// Params that never reach the wire are dropped so they cannot change the key.
const getCanonicalQuery = (request: IdempotencyRequest): string => {
  const query = Object.entries(request.query ?? {}).filter(
    ([, value]) => value !== undefined && !(Array.isArray(value) && value.length === 0),
  )

  const { searchParams } = getRequestUrl(request)
  searchParams.sort()

  return canonicalizeJson({ query: Object.fromEntries(query), search: [...searchParams] })
}

/* The backend validates the key as a UUID, so the digest is re-shaped into RFC 9562 v8 — the version
   reserved for vendor-defined, hash-derived ids. Deriving it keeps the key deterministic, which a
   random UUID could not be: a double-click has to produce the same key twice with nothing stored. */
const toUuidV8 = (hex: string): string => {
  const variant = ((parseInt(hex.slice(16, 17), 16) & 0x3) | 0x8).toString(16)

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-8${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`
}

const getCanonicalBody = ({ body }: IdempotencyRequest): string | null =>
  body === undefined || body === null ? null : canonicalizeJson(body)

export const getIdempotencyRequestSignature = async (request: IdempotencyRequest): Promise<string | null> => {
  if (!isIdempotencyEligible(request) || hasExplicitIdempotencyKey(request)) return null

  let identity: string
  try {
    identity = JSON.stringify([
      getMethod(request),
      normalizePath(request),
      getCanonicalQuery(request),
      getCanonicalBody(request),
    ])
  } catch {
    return null
  }

  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity))
  const hex = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')

  return toUuidV8(hex)
}
